"use client";

import { default as NextLink, type LinkProps } from "next/link";
import { forwardRef, type PropsWithChildren } from "react";
import { buttonVariants } from "./buttons/button";
import { cn } from "./";
import { type VariantProps } from "class-variance-authority";
import { usePathname } from "next/navigation";

type CustomLinkProps = PropsWithChildren<LinkProps & React.AnchorHTMLAttributes<HTMLAnchorElement>> & {
  variant?: VariantProps<typeof buttonVariants>["variant"];
  size?: VariantProps<typeof buttonVariants>["size"];
};

const Link = forwardRef<HTMLAnchorElement, CustomLinkProps>(({ variant, size, href, className, ...props }, ref) => {
  const pathname = usePathname();

  return (
    <NextLink
      {...props}
      href={href}
      ref={ref}
      prefetch={props.prefetch ?? false}
      onClick={(e) => {
        if (props.onClick) return props.onClick(e);

        if (!href) return;

        const targetUrl = typeof href === "string" ? href : (href as URL).toString();

        // Allow fragment links (e.g., #section-id) to navigate normally
        // This preserves query parameters and enables smooth scrolling
        if (targetUrl.startsWith("#")) {
          return;
        }

        // Convert href to absolute URL for comparison
        const searchParams = new URLSearchParams(window.location.search);
        const isSearchOnly = !href.includes("/");
        const currentUrl = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

        // Parse URL for comparison
        const current = new URL(currentUrl, window.location.origin);
        const target = new URL(targetUrl, window.location.origin);

        const samePath = current.pathname === target.pathname;
        const sameSearch = current.search === target.search;

        // Same path or the url only contains target string.
        if (samePath || isSearchOnly) {
          e.preventDefault();
          e.stopPropagation();

          // If same URL entirely (including search), do nothing
          if (sameSearch) return;

          window.history.pushState(null, "", `${target.search[0] === "?" ? "" : "?"}${target.search}`);
          return;
        }
      }}
      className={cn(variant || size ? buttonVariants({ variant, size }) : "", className)}
    />
  );
});

Link.displayName = "Link";

export default Link;
